<template>
  <div class="sa-credits">
    <h1 class="sa-credits-h1">Voice minutes</h1>

    <!-- Loading. Says nothing about numbers, because "0 remaining" and "not
         loaded yet" are different facts and the screen must not confuse them. -->
    <p v-if="loading" class="sa-credits-loading">Loading this lab&rsquo;s usage&hellip;</p>

    <!-- Could not load. Not a modal and not a notification: the user came here
         on purpose, so the answer belongs on the page they asked for. -->
    <section v-else-if="loadError" class="sa-credits-panel sa-credits-panel-danger">
      <p class="sa-credits-panel-title">Voice minutes not shown.</p>
      <p class="sa-credits-panel-detail">{{ loadError.detail }}</p>
      <p class="sa-credits-panel-next">{{ loadError.next }}</p>
      <button type="button" class="sa-credits-btn" @click="load">Try again</button>
    </section>

    <!-- STATE 1 — the lab is not in the beta. No numbers at all: a usage figure
         for a feature the lab cannot use is noise, and an allowance it does not
         have would read as a promise. -->
    <section v-else-if="!credits.voiceEnabled" class="sa-credits-panel">
      <p class="sa-credits-panel-title">Voice control is not enabled for this lab.</p>
      <p class="sa-credits-panel-detail">
        Voice is an invite-only beta while it is being tested.
      </p>
      <p class="sa-credits-panel-next">
        Ask your lab admin to request access for this lab.
      </p>
    </section>

    <template v-else>
      <p class="sa-credits-period">{{ periodLabel }}</p>

      <!-- STATE 2 — unlimited. `allowanceMinutes` 0 or absent while the beta is
           on means no allowance has been set yet, which is how the first beta
           labs run. Rendering "0 remaining" here would be exactly backwards. -->
      <section v-if="unlimited" class="sa-credits-panel">
        <p class="sa-credits-headline">Unlimited</p>
        <p class="sa-credits-panel-detail">
          No monthly limit is set for this lab.
        </p>
        <dl class="sa-credits-figures">
          <div class="sa-credits-figure">
            <dt>Used this period</dt>
            <dd>{{ formatMinutes(credits.usedMinutes) }}</dd>
          </div>
          <div class="sa-credits-figure">
            <dt>Sessions</dt>
            <dd>{{ credits.sessions ?? 0 }}</dd>
          </div>
        </dl>
      </section>

      <!-- STATES 3 and 4 — a real allowance. -->
      <section
        v-else
        class="sa-credits-panel"
        :class="{ 'sa-credits-panel-warning': credits.exhausted }"
      >
        <!-- State 4 leads with the fact, before the figures. A lab that has run
             out needs to read that first, not work it out from a subtraction. -->
        <p v-if="credits.exhausted" class="sa-credits-headline">
          No minutes left this period.
        </p>
        <p v-else class="sa-credits-headline">
          {{ formatMinutes(credits.remainingMinutes) }} left
        </p>

        <!-- Overage is shown, never hidden. Enforcement happens when a session
             is started, so an established session can always run past the line;
             pretending otherwise would make the screen lie about the total. -->
        <p v-if="overage > 0" class="sa-credits-overage">
          {{ formatMinutes(overage) }} over the allowance.
          A session that had already started was allowed to finish.
        </p>

        <dl class="sa-credits-figures">
          <div class="sa-credits-figure">
            <dt>Allowance</dt>
            <dd>{{ formatMinutes(credits.allowanceMinutes) }}</dd>
          </div>
          <div class="sa-credits-figure">
            <dt>Used</dt>
            <dd>{{ formatMinutes(credits.usedMinutes) }}</dd>
          </div>
          <div class="sa-credits-figure">
            <dt>Remaining</dt>
            <dd>{{ formatMinutes(credits.remainingMinutes) }}</dd>
          </div>
          <div class="sa-credits-figure">
            <dt>Sessions</dt>
            <dd>{{ credits.sessions ?? 0 }}</dd>
          </div>
        </dl>

        <p v-if="credits.exhausted" class="sa-credits-panel-next">
          Minutes reset {{ resetLabel }}. Ask your lab admin to raise the
          allowance.
        </p>
      </section>

      <!-- History. Lab admins only on the server, so its absence is normal for
           an ordinary member and must not read as a failure. -->
      <section v-if="history && history.length" class="sa-credits-history">
        <h2 class="sa-credits-h2">Last {{ history.length }} periods</h2>
        <ul class="sa-credits-history-list">
          <li v-for="row in history" :key="row.periodStart" class="sa-credits-history-row">
            <span class="sa-credits-history-month">{{ monthLabel(row.periodStart) }}</span>
            <span class="sa-credits-history-bar" aria-hidden="true">
              <span
                class="sa-credits-history-fill"
                :class="{ 'sa-credits-history-fill-over': isOver(row) }"
                :style="{ width: barWidth(row) }"
              />
            </span>
            <span class="sa-credits-history-value">
              {{ formatMinutes(row.usedMinutes) }}
              <span class="sa-credits-history-sub">
                {{ row.sessions ?? 0 }} session{{ (row.sessions ?? 0) == 1 ? "" : "s" }}
              </span>
            </span>
          </li>
        </ul>
      </section>
    </template>
  </div>
</template>

<script>
export default {
  name: "VoiceCredits",
  data() {
    return { loading: true, loadError: null };
  },
  computed: {
    credits() {
      return this.$store.state.voiceCredits || {};
    },
    history() {
      return this.$store.state.voiceCreditsHistory;
    },
    // 0 or absent allowance while the beta is on means unlimited — that is the
    // contract, and it is how a lab runs before anyone has priced it.
    unlimited() {
      return !Number(this.credits.allowanceMinutes);
    },
    overage() {
      return Number(this.credits.overageMinutes) || 0;
    },
    periodLabel() {
      const start = this.credits.periodStart;
      if (!start) return "";
      return `Period beginning ${this.formatDate(start)}`;
    },
    resetLabel() {
      return this.credits.periodEnd
        ? `on ${this.formatDate(this.credits.periodEnd)}`
        : "next period";
    },
    // Bars are relative to the largest thing on the chart, allowance included,
    // so a period that ran over is visibly wider than its own allowance.
    historyMax() {
      if (!this.history || !this.history.length) return 0;
      return this.history.reduce(
        (max, row) =>
          Math.max(max, Number(row.usedMinutes) || 0, Number(row.allowanceMinutes) || 0),
        0
      );
    },
  },
  methods: {
    formatMinutes(value) {
      // An absent figure is NOT zero. `remainingMinutes` is null for an
      // unlimited lab, and a missing or malformed field would otherwise be
      // rendered as a confident "0 min" — a fabricated number, and the one
      // number most likely to be misread as "you have run out".
      if (value === null || value === undefined || value === "") return "—";
      const minutes = Number(value);
      if (!Number.isFinite(minutes)) return "—";
      const rounded = Math.round(minutes * 10) / 10;
      const shown = Number.isInteger(rounded) ? rounded : rounded.toFixed(1);
      return `${shown} min`;
    },
    formatDate(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    monthLabel(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleDateString(undefined, {
        month: "short",
        year: "2-digit",
      });
    },
    isOver(row) {
      const allowance = Number(row.allowanceMinutes) || 0;
      return allowance > 0 && (Number(row.usedMinutes) || 0) > allowance;
    },
    barWidth(row) {
      if (!this.historyMax) return "0%";
      const pct = ((Number(row.usedMinutes) || 0) / this.historyMax) * 100;
      return `${Math.max(0, Math.min(100, pct))}%`;
    },

    load() {
      this.loading = true;
      this.loadError = null;

      this.$store
        .dispatch("fetchVoiceCredits")
        .then(() => {
          this.loading = false;
          // History is lab-admin only. A 403 here is the ordinary answer for an
          // ordinary member, so it is swallowed: the section simply does not
          // appear, and nothing on screen claims a failure.
          return this.$store.dispatch("fetchVoiceCreditsHistory").catch(() => {});
        })
        .catch((err) => {
          this.loading = false;
          this.loadError = {
            detail: err?.detail || "Your device could not reach the server.",
            next: err?.next || "Check your connection and try again.",
          };
        });
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
/* Plain CSS: public/assets/styles.css is a committed, pre-compiled artefact and
   the Vue build does not regenerate it, so an unlisted Tailwind class would do
   nothing at runtime. */
.sa-credits {
  max-width: 40rem;
  margin: 0 auto;
  padding: 1rem 0.875rem 3rem;
  font-family: Montserrat, sans-serif;
  color: #0c4a6e;
  text-align: left;
}

.sa-credits-h1 {
  margin: 0 0 0.25rem;
  font-size: 22px;
  font-weight: 700;
}

.sa-credits-h2 {
  margin: 0 0 0.5rem;
  font-size: 16px;
  font-weight: 600;
}

.sa-credits-period {
  margin: 0 0 1rem;
  font-size: 13px;
  color: #475569;
}

.sa-credits-loading {
  margin: 1rem 0;
  font-size: 15px;
  color: #475569;
}

.sa-credits-panel {
  border: 2px solid #0c4a6e;
  border-radius: 4px;
  padding: 1rem;
  background: #ffffff;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -2px rgba(0, 0, 0, 0.1);
}

/* Out of minutes is a refusal, not a fault: the system is working correctly.
   Warning, never danger (DESIGN.md section 2). */
.sa-credits-panel-warning {
  border-color: #facc15;
  background: #fefce8;
}

.sa-credits-panel-danger {
  border-color: rgb(248 113 113);
  background: #fef2f2;
}

.sa-credits-headline {
  margin: 0 0 0.5rem;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
}

.sa-credits-panel-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.sa-credits-panel-detail {
  margin: 0.25rem 0 0;
  font-size: 14px;
  line-height: 1.45;
}

.sa-credits-panel-next {
  margin: 0.5rem 0 0;
  font-size: 13px;
  color: #475569;
  line-height: 1.45;
}

.sa-credits-overage {
  margin: 0 0 0.75rem;
  padding: 0.5rem 0.625rem;
  border-left: 4px solid #facc15;
  background: #ffffff;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
}

.sa-credits-figures {
  margin: 0.875rem 0 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.sa-credits-figure dt {
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  color: #475569;
}

.sa-credits-figure dd {
  margin: 0;
  font-size: 19px;
  font-weight: 600;
}

.sa-credits-btn {
  margin-top: 0.875rem;
  min-height: 52px;
  padding: 0.75rem 1.5rem;
  border: 2px solid #0c4a6e;
  border-radius: 9999px;
  background: #ffffff;
  color: #0c4a6e;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}
.sa-credits-btn:focus-visible {
  outline: 3px solid #0c4a6e;
  outline-offset: 2px;
}

.sa-credits-history {
  margin-top: 1.5rem;
}

.sa-credits-history-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.sa-credits-history-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.375rem 0;
}

.sa-credits-history-month {
  flex: none;
  width: 4.25rem;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
}

.sa-credits-history-bar {
  flex: 1 1 auto;
  height: 12px;
  border-radius: 2px;
  background: #e2e8f0;
  overflow: hidden;
}

.sa-credits-history-fill {
  display: block;
  height: 100%;
  background: #0c4a6e;
}

/* A period that ran past its allowance is marked, not just longer. Never colour
   alone: the minutes are printed next to it either way. */
.sa-credits-history-fill-over {
  background: #facc15;
}

.sa-credits-history-value {
  flex: none;
  width: 6.5rem;
  text-align: right;
  font-size: 13px;
  font-weight: 600;
}

.sa-credits-history-sub {
  display: block;
  font-size: 11px;
  font-weight: 400;
  color: #475569;
}

@media (min-width: 480px) {
  .sa-credits-figures {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
</style>
